"use client"

import { useEffect, useMemo, useState } from "react"
import type { TabNode } from "flexlayout-react"
import { useEditorStore } from "@/lib/editor-store"
import { isImageFile, isRenderableImageSrc, normalizePath } from "@/lib/compiler/path-utils"

interface ImagePreviewPanelProps {
  node: TabNode
  fileId?: string
}

export function ImagePreviewPanel({ node, fileId = "" }: ImagePreviewPanelProps) {
  const { state, getFolderId } = useEditorStore()
  const [loadFailed, setLoadFailed] = useState(false)
  const [retry, setRetry] = useState(0)

  const configFileId = (node.getConfig() as { fileId?: string } | undefined)?.fileId
  const path = normalizePath(configFileId || fileId)
  const fileRecord = path ? state.files[path] : undefined
  const content = fileRecord?.content?.trim() ?? ""
  const inlineSrc = content.startsWith("data:image/") || content.startsWith("blob:") ? content : ""
  // Signed storage URLs embed the object name. A rename keeps the file id and
  // invalidates that URL, so auth previews are loaded by id through the proxy.
  const backendId = path ? getFolderId(path) : undefined
  const proxySrc = backendId
    ? `/api/files/preview-proxy?fileId=${encodeURIComponent(backendId)}&v=${encodeURIComponent(path)}&r=${retry}`
    : ""
  const signedSrc = !proxySrc && isRenderableImageSrc(content) ? content : ""
  const sessionSrc =
    !inlineSrc && !proxySrc && !signedSrc && state.sessionId && path
      ? `/api/compiler/sessions/${encodeURIComponent(state.sessionId)}/assets?path=${encodeURIComponent(path)}`
      : ""

  const src = inlineSrc || proxySrc || signedSrc || sessionSrc
  const waitingForPreview = Boolean(path) && !src && isImageFile(path)

  useEffect(() => {
    setLoadFailed(false)
    setRetry(0)
  }, [path, backendId])

  const checkerboardStyle = useMemo(
    () => ({
      backgroundImage:
        "linear-gradient(45deg, #efefef 25%, transparent 25%), linear-gradient(-45deg, #efefef 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #efefef 75%), linear-gradient(-45deg, transparent 75%, #efefef 75%)",
      backgroundSize: "20px 20px",
      backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0",
    }),
    [],
  )

  return (
    <div className="flex h-full w-full items-center justify-center bg-[var(--ed-preview-img-bg)] overflow-auto p-4 select-none">
      {src && !loadFailed ? (
        <div className="max-w-full max-h-full flex flex-col items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={node.getName()}
            className="max-w-full max-h-[70vh] object-contain shadow-lg rounded-lg border border-[var(--ed-preview-img-border)] bg-white"
            style={checkerboardStyle}
            onError={() => {
              if (backendId && retry < 1) {
                setRetry(1)
                return
              }
              setLoadFailed(true)
            }}
          />
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          {waitingForPreview
            ? "Loading image…"
            : loadFailed
              ? "Failed to load image"
              : "No image content"}
        </div>
      )}
    </div>
  )
}
